import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import {
  BarChart3,
  ChevronDown,
  ChevronUp,
  Flag,
  Play,
} from "../../../../components/icons";
import { MatchEvent } from "../../../../store/useMatchLogStore";
import type { GoalPlacement } from "../../utils/goalFrame";
import { CardSelection } from "../molecules/QuickCardPanel";
import MatchTimerDisplay from "../molecules/MatchTimerDisplay";
import RefereeActionBar from "../molecules/RefereeActionBar";
import TeamSelector from "../molecules/TeamSelector";
import type { CockpitViewMode, TeamColorInfo } from "../molecules/TeamSelector";
import InstructionBanner from "../molecules/InstructionBanner";
import ScoreBoard from "../molecules/ScoreBoard";
import MatchPeriodSelector from "../molecules/MatchPeriodSelector";
import type { PeriodPhase } from "../../hooks/usePeriodManager";
import type { Match } from "../../types";
// PMA-COCK-111: LiveEventFeed no longer renders on the cockpit path (see the
// removed "LIVE EVENT FEED" block below) — import dropped accordingly. The
// component itself is untouched at
// src/pages/logger/components/molecules/LiveEventFeed.tsx.
import ActionStage from "./ActionStage";
import AnalyticsView from "./AnalyticsView";
import { canResumeEffective } from "../../lib/clockStateMachine";
import type { PositionMode } from "../../hooks/useActionFlow";
import type {
  TacticalPosition,
  Formation,
} from "../../hooks/useTacticalPositions";

interface LoggerViewProps {
  match: any;
  /**
   * PMA-COCK-210 NOT_STARTED/G-03 addendum (EPIC-61, T15, lead product
   * decision 2026-09-19): `useCockpitStatusProjection.ts`'s status-projected
   * match (a zeroed Fulltime/Completed match reads as Pending here) — the
   * SAME value `usePeriodManager` already consumes for `currentPhase`. Given
   * to `MatchTimerDisplay` ONLY (never the raw `match` above) so the two
   * components agree on "what state is this match in" instead of one seeing
   * a real answer and the other a stale one — the exact "one fact, two
   * disagreeing readers" shape this project has been bitten by before (the
   * stale-baseline clock, the baseline/timestamp pair). Every other reader
   * in this file (ScoreBoard, MatchPeriodSelector via `currentPhase`, etc.)
   * is unaffected — this prop exists for one call site's one internal
   * check, not a second general-purpose match object.
   */
  matchForPhase: any;
  operatorPeriod: number;
  globalClock: string;
  /** Broadcast-frame stamp clock (same frame events' match_clock use). */
  broadcastMatchClock: string;
  effectiveClock: string;
  ineffectiveClock: string;
  varTimeClock: string;
  timeoutTimeClock: string;
  clockMode: "EFFECTIVE" | "INEFFECTIVE";
  isGlobalClockRunning: boolean;
  cockpitLocked: boolean;
  lockReason?: string;
  handleGlobalClockStartGuarded: () => void;
  handleGlobalClockStopGuarded: () => void;
  handleModeSwitchGuarded: (mode: "EFFECTIVE" | "INEFFECTIVE") => void;
  handleVarToggle: () => void;
  handleTimeoutToggle: () => void;
  handleRefereeAction: (actionId: string) => void;
  isVarActive: boolean;
  isTimeoutActive: boolean;
  showFieldResume: boolean;
  isHalftimePhase?: boolean;
  hasActiveIneffective?: boolean;
  ineffectiveTeamLabel?: string;
  /** Broadcast-cap main clock, e.g. "56:00" — caps at each period's regulation end. */
  broadcastMainClock: string;
  /** Broadcast-cap added time, e.g. "+02:05", or "" when none. */
  broadcastAddedClock: string;
  currentStep: any;
  selectedPlayer: any;
  selectedAction: string | null;
  pendingCardType: CardSelection | null;
  fieldAnchor: any;
  onFieldIds: { home: Set<string>; away: Set<string> };
  selectedTeam: "home" | "away" | "both";
  expelledPlayerIds: Set<string>;
  cardDisciplinaryStatus: Record<string, { yellowCount: number; red: boolean }>;
  /** PMA-COCK-145: per-player goal count feeding the "En cancha" goal-ball indicator. */
  goalCountByPlayerId?: Record<string, number>;
  setSelectedTeam: (team: "home" | "away" | "both") => void;
  handlePlayerSelection: (...args: any[]) => void;
  handleFieldPlayerSelection: (...args: any[]) => void;
  handleFieldDestination: (location: any) => void;
  handleZoneSelect: (zoneId: number) => void;
  handleQuickActionSelect: (action: string) => void;
  handleOpenMoreActions: () => void;
  resetFlow: () => void;
  handleQuickSubstitution: (team: "home" | "away") => void;
  handleCardSelection: (cardType: CardSelection) => void;
  cancelCardSelection: () => void;
  availableActions: string[];
  isSubmitting: boolean;
  handleActionClickOverride: (...args: any[]) => void;
  availableOutcomes: string[];
  handleOutcomeSelect: (...args: any[]) => void;
  currentTeam: any;
  eligibleRecipients: any[];
  handleRecipientSelect: (...args: any[]) => void;
  handleUndoLastEvent: () => void;
  undoDisabled: boolean;
  undoCount?: number;
  manualFieldFlip: boolean;
  setManualFieldFlip: (value: boolean | ((prev: boolean) => boolean)) => void;
  priorityPlayerId: string | null;
  liveEvents: MatchEvent[];
  duplicateHighlight: any;
  handleDeletePendingEvent: (...args: any[]) => void;
  handleDeleteLoggedEvent: (...args: any[]) => void;
  isAdmin: boolean;
  handleUpdateEventNotes: (...args: any[]) => Promise<void> | void;
  handleUpdateEventData?: (
    event: MatchEvent,
    updates: Partial<MatchEvent>,
  ) => Promise<void> | void;
  /** R1 / PMA-COCK-076: dedicated one-tap VAR correction, Corner Kick -> Goal
   * Kick in place. Not gated by isAdmin. Declared here for the caller's prop
   * contract but no longer consumed — its trigger lived in LiveEventFeed's
   * event row, which PMA-COCK-111 removed from the cockpit's render path
   * (2026-08-14) and ReviewView.tsx never implemented an equivalent. */
  handleVarCornerToGoalKick?: (event: MatchEvent) => Promise<void> | void;
  /** PMA-COCK-182: review/feed goal-mouth placement edit, same non-consumed
   * prop-contract situation as `handleVarCornerToGoalKick` above — declared
   * for the caller, not destructured here, since `LiveEventFeed` has had no
   * mount site on the cockpit path since PMA-COCK-111. */
  handleUpdateGoalPlacement?: (
    event: MatchEvent,
    placement: GoalPlacement,
  ) => Promise<void> | void;
  getDisplayPosition?: (
    playerId: string,
    flipSides: boolean,
  ) => TacticalPosition;
  onTacticalPlayerDragEnd?: (
    playerId: string,
    displayX: number,
    displayY: number,
    playerPosition: string | undefined,
    side: "home" | "away",
  ) => void;
  draggingPlayerId?: string | null;
  onTacticalDragStart?: (playerId: string) => void;
  onTacticalDragStop?: () => void;
  allTacticalPositions?: Map<string, TacticalPosition>;
  homeFormation?: Formation | null;
  awayFormation?: Formation | null;
  applyFormation?: (side: "home" | "away", formation: Formation | null) => void;
  viewMode?: CockpitViewMode;
  setViewMode?: (mode: CockpitViewMode) => void;
  homeTeamColorInfo?: TeamColorInfo;
  awayTeamColorInfo?: TeamColorInfo;
  onTeamColorSaved?: (
    teamId: string,
    primary: string | null,
    secondary: string | null,
  ) => void;
  /** When true (live match), expand drag bounds to full field. */
  isMatchLive?: boolean;
  positionMode: PositionMode;
  onPositionModeChange: (mode: PositionMode) => void;
  /** PMA-COCK-093: the 5 props AnalyticsView needs beyond what LoggerView
   * already receives, threaded through from LoggerCockpit so the inline
   * analytics disclosure below the pitch can mount the same organism the
   * full-screen analytics view mode uses. */
  queuedEvents: MatchEvent[];
  effectiveTime: number;
  varTimeSeconds: number;
  timeoutTimeSeconds: number;
  ineffectiveBreakdown: any;
  /** PMA-COCK-106 (3rd rail-scope revision): ScoreBoard's own props,
   * threaded here (mirrors the "prop threading only" pattern already used
   * for PMA-COCK-093's AnalyticsView props above) so ScoreBoard can mount
   * inside the rail instead of `CockpitTopSection.tsx` when `isRailLayout`
   * is true. */
  liveScore: { home: number; away: number };
  queuedCount: number;
  pendingAckCount: number;
  transitionError: string | null;
  toastMessage?: string;
  /** PMA-COCK-106: true at the landscape-tablet breakpoint
   * (`(max-width: 1180px) and (orientation: landscape)`,
   * `LoggerCockpit.tsx`'s `useMediaQuery` call). Rail scope, 3rd revision —
   * see `LoggerCockpit.tsx`'s own `isRailLayout` comment for the full v1/
   * v2/v3 history. Currently: `ScoreBoard` + `MatchTimerDisplay` +
   * `RefereeActionBar` relocate beside the pitch when true; `TeamSelector`
   * + `InstructionBanner` stay stacked above the pitch in BOTH layouts,
   * unconditionally — `TeamSelector`'s formation pickers are structurally
   * two-sided, built to sit near their own half of the pitch, a mismatch
   * with a one-sided rail regardless of space. Unaffected at every other
   * viewport (portrait, desktop, phone). */
  isRailLayout: boolean;
  /** EPIC-111 UIJ-R2-1: portrait tablet (< 1024px wide, no rail). The period
   * ladder, referee actions and the step hint move BELOW the pitch and the
   * clock panel becomes a two-row strip, so the pitch starts near the top. */
  isPortraitStack?: boolean;
  /** PMA-COCK-210 rail relocation (EPIC-61, client wave 2026-09-19, lead
   * decision 10 — "el de arriba" (the top banner) is the client's actual
   * complaint, finger travel on a tablet during live play; the phase ladder
   * must physically move into the rail, not stay in `CockpitTopSection.tsx`).
   * Every value below already exists at `LoggerCockpit.tsx`'s own scope and
   * was already threaded to `<CockpitTopSection>`'s identical prop of the
   * same name — pure prop-threading, no new state/logic added there. The
   * `guardTransition` wrapping each `onTransitionToX`/`onFinishMatch`
   * callback below mirrors `CockpitTopSection.tsx`'s own
   * `<MatchPeriodSelector>` call site byte-for-byte (mandatory: every other
   * transition control on this page goes through `guardTransition` — see
   * PMA-COCK-209, the defect class this same wave found and fixed for the
   * ONE control that used to skip it). `CockpitTopSection.tsx`'s own ladder
   * is suppressed via `hideMatchPeriodSelector={isRailLayout}` (mirrors the
   * pre-existing `hideScoreBoard` pattern) so the two mounts are never both
   * live at once — same single-mount-either-way discipline as ScoreBoard. */
  currentPhase: PeriodPhase;
  isExtraTime: boolean;
  extraTimeSeconds: number;
  guardTransition: (target: Match["status"], fn?: () => void) => void;
  transitionToHalftime: () => void;
  transitionToSecondHalf: () => void;
  transitionToFulltime: () => void;
  transitionToExtraFirst: () => void;
  transitionToExtraHalftime: () => void;
  transitionToExtraSecond: () => void;
  transitionToPenalties: () => void;
  finishMatch: () => void;
  transitionDisabled: boolean;
  penaltiesTransitionDisabled: boolean;
  transitionReason: string | undefined;
  t: any;
}

export default function LoggerView({
  match,
  matchForPhase,
  operatorPeriod,
  globalClock,
  broadcastMatchClock,
  effectiveClock,
  ineffectiveClock,
  varTimeClock,
  timeoutTimeClock,
  clockMode,
  isGlobalClockRunning,
  cockpitLocked,
  lockReason,
  handleGlobalClockStartGuarded,
  handleGlobalClockStopGuarded,
  handleModeSwitchGuarded,
  handleVarToggle,
  handleTimeoutToggle,
  handleRefereeAction,
  isVarActive,
  isTimeoutActive,
  showFieldResume,
  isHalftimePhase = false,
  hasActiveIneffective,
  ineffectiveTeamLabel,
  broadcastMainClock,
  broadcastAddedClock,
  currentStep,
  selectedPlayer,
  selectedAction,
  pendingCardType,
  fieldAnchor,
  onFieldIds,
  selectedTeam,
  expelledPlayerIds,
  cardDisciplinaryStatus,
  goalCountByPlayerId,
  setSelectedTeam,
  handlePlayerSelection,
  handleFieldPlayerSelection,
  handleFieldDestination,
  handleZoneSelect,
  handleQuickActionSelect,
  handleOpenMoreActions,
  resetFlow,
  handleQuickSubstitution,
  handleCardSelection,
  cancelCardSelection,
  availableActions,
  isSubmitting,
  handleActionClickOverride,
  availableOutcomes,
  handleOutcomeSelect,
  currentTeam,
  eligibleRecipients,
  handleRecipientSelect,
  handleUndoLastEvent,
  undoDisabled,
  undoCount,
  manualFieldFlip,
  setManualFieldFlip,
  priorityPlayerId,
  liveEvents,
  // PMA-COCK-111: duplicateHighlight/handleDeletePendingEvent/
  // handleDeleteLoggedEvent/handleUpdateEventNotes/handleVarCornerToGoalKick
  // are still declared on LoggerViewProps below (unchanged prop contract for
  // the caller) but deliberately not destructured — LiveEventFeed no longer
  // renders on the cockpit path, so this component has nothing to pass them
  // to. The Revision/Review tab (ReviewView.tsx) gets its own equivalent
  // handlers directly, independent of this file.
  isAdmin,
  handleUpdateEventData,
  getDisplayPosition,
  onTacticalPlayerDragEnd,
  draggingPlayerId,
  onTacticalDragStart,
  onTacticalDragStop,
  allTacticalPositions,
  homeFormation,
  awayFormation,
  applyFormation,
  viewMode,
  setViewMode,
  homeTeamColorInfo,
  awayTeamColorInfo,
  onTeamColorSaved,
  isMatchLive = false,
  positionMode,
  onPositionModeChange,
  queuedEvents,
  effectiveTime,
  varTimeSeconds,
  timeoutTimeSeconds,
  ineffectiveBreakdown,
  liveScore,
  queuedCount,
  pendingAckCount,
  transitionError,
  toastMessage,
  isRailLayout,
  isPortraitStack = false,
  currentPhase,
  isExtraTime,
  extraTimeSeconds,
  guardTransition,
  transitionToHalftime,
  transitionToSecondHalf,
  transitionToFulltime,
  transitionToExtraFirst,
  transitionToExtraHalftime,
  transitionToExtraSecond,
  transitionToPenalties,
  finishMatch,
  transitionDisabled,
  penaltiesTransitionDisabled,
  transitionReason,
  t,
}: LoggerViewProps) {
  // PMA-COCK-144: the floating field-overlay "Reanudar Tiempo Efectivo"
  // button below (headerAction) is the OTHER of the two global-resume render
  // sites (MatchTimerDisplay.tsx owns its own, separately guarded). Must be
  // rejected identically while a scoped stoppage (VAR/timeout) is open — the
  // handler-level rejection is the actual rule (useCockpitClockHandlers.ts's
  // handleModeSwitchGuarded); this is only the UI affordance half.
  const resumeBlockedByScopedStoppage = !canResumeEffective({
    clockMode,
    isVarActive,
    isTimeoutActive,
  });
  const [isInlineAnalyticsOpen, setIsInlineAnalyticsOpen] = useState(false);
  /** PMA-COCK-106: collapsed-by-default disclosure for `RefereeActionBar` at
   * the portrait-tablet breakpoint ONLY (max-[1180px], no rail available —
   * see the two-column rail's own comment for why portrait can't reuse it).
   * Desktop (>=1180px) never renders the toggle at all (`hidden
   * max-[1180px]:flex`) and always shows the bar — byte-for-byte unchanged.
   * Mirrors the existing `isInlineAnalyticsOpen` disclosure pattern
   * (PMA-COCK-093) above for visual/interaction consistency. */
  const [showRefereeBar, setShowRefereeBar] = useState(false);
  /** PMA-COCK-106 (3rd revision): the rail's scroll-fade affordance. Lead's
   * explicit ruling: add it ONLY if the rail's content genuinely exceeds
   * its viewport-bounded window — never speculatively. With ScoreBoard +
   * MatchTimerDisplay + RefereeActionBar restored, that condition is real
   * at 1024x768 (measured: RefereeActionBar's own single-column stack, the
   * only correct layout at this ~200px width, straddles the fold). Detected
   * dynamically (scrollHeight > clientHeight, hidden once scrolled to the
   * bottom) rather than hardcoded, so a future content change that makes
   * the rail fit again automatically stops showing it — the same
   * measure-don't-assume discipline used throughout this rail's tuning. */
  const isShortViewport = useMediaQuery(
    "(max-height: 899px) and (min-width: 1100px)",
  );
  const railRef = useRef<HTMLDivElement | null>(null);
  const [railHasMoreBelow, setRailHasMoreBelow] = useState(false);

  useEffect(() => {
    if (!isRailLayout) {
      setRailHasMoreBelow(false);
      return;
    }
    const el = railRef.current;
    if (!el) return;
    const update = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight + 1;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      setRailHasMoreBelow(hasOverflow && !atBottom);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [isRailLayout]);
  /** PMA-COCK-106: `ActionStage` (the pitch) is identical in both the rail
   * and stacked layouts below — only what surrounds it differs. Defined ONCE
   * here (not inlined twice) so its full, and long, prop list is written a
   * single time — duplicating a ~50-prop call by hand across two JSX
   * branches is exactly the kind of edit that silently drops a prop, which
   * would break the cockpit in whichever branch got it wrong. This is the
   * exact, unmodified prop list that was already here pre-PMA-COCK-106. */
  const actionStageElement = (
    <ActionStage
      match={match}
      manualFieldFlip={manualFieldFlip}
      selectedPlayer={selectedPlayer}
      selectedTeam={selectedTeam}
      expelledPlayerIds={expelledPlayerIds}
      cardDisciplinaryStatus={cardDisciplinaryStatus}
      goalCountByPlayerId={goalCountByPlayerId}
      pendingCardType={pendingCardType}
      setSelectedTeam={setSelectedTeam}
      onFieldIds={onFieldIds}
      handlePlayerSelection={handlePlayerSelection}
      handleFieldPlayerSelection={handleFieldPlayerSelection}
      handleFieldDestination={handleFieldDestination}
      handleZoneSelect={handleZoneSelect}
      currentStep={currentStep}
      cockpitLocked={cockpitLocked}
      fieldAnchor={fieldAnchor}
      handleQuickActionSelect={handleQuickActionSelect}
      handleOpenMoreActions={handleOpenMoreActions}
      resetFlow={resetFlow}
      showFieldResume={showFieldResume}
      priorityPlayerId={priorityPlayerId}
      isGlobalClockRunning={isGlobalClockRunning}
      clockMode={clockMode}
      isVarActive={isVarActive}
      handleQuickSubstitution={handleQuickSubstitution}
      handleCardSelection={handleCardSelection}
      cancelCardSelection={cancelCardSelection}
      availableActions={availableActions}
      isSubmitting={isSubmitting}
      handleActionClickOverride={handleActionClickOverride}
      selectedAction={selectedAction}
      availableOutcomes={availableOutcomes}
      handleOutcomeSelect={handleOutcomeSelect}
      currentTeam={currentTeam}
      eligibleRecipients={eligibleRecipients}
      handleRecipientSelect={handleRecipientSelect}
      getDisplayPosition={getDisplayPosition}
      onTacticalPlayerDragEnd={onTacticalPlayerDragEnd}
      draggingPlayerId={draggingPlayerId}
      onTacticalDragStart={onTacticalDragStart}
      onTacticalDragStop={onTacticalDragStop}
      allTacticalPositions={allTacticalPositions}
      homeFormation={homeFormation}
      awayFormation={awayFormation}
      applyFormation={applyFormation}
      hasActiveIneffective={hasActiveIneffective}
      isMatchLive={isMatchLive}
      positionMode={positionMode}
      onPositionModeChange={onPositionModeChange}
      homeTeamColorInfo={homeTeamColorInfo}
      awayTeamColorInfo={awayTeamColorInfo}
      onTeamColorSaved={onTeamColorSaved}
      headerAction={
        showFieldResume ? (
          <button
            type="button"
            data-testid="btn-resume-effective"
            onClick={() => handleModeSwitchGuarded("EFFECTIVE")}
            disabled={cockpitLocked || resumeBlockedByScopedStoppage}
            title={
              resumeBlockedByScopedStoppage
                ? t(
                    "resumeBlockedScopedStoppage",
                    "Finish the active VAR or timeout first",
                  )
                : undefined
            }
            className="flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 border border-emerald-500 rounded-lg font-semibold text-xs uppercase tracking-wide transition-colors shadow-md shadow-emerald-900/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Play size={14} aria-hidden="true" />
            {t("resumeEffective", "Resume Effective Time")}
          </button>
        ) : null
      }
      t={t}
    />
  );
  const instructionBannerElement = (
    <InstructionBanner
      t={t}
      currentStep={currentStep}
      selectedPlayer={selectedPlayer}
      selectedAction={selectedAction}
      cardSelection={
        pendingCardType
          ? pendingCardType === "Yellow"
            ? t("cardSelectYellow", "Yellow")
            : pendingCardType === "Red"
              ? t("cardSelectRed", "Red")
              : t("cardSelectCancel", "Cancel")
          : null
      }
    />
  );
  const refereeSectionElement = (
    <>
      {/* Referee actions — full width at desktop, unchanged. At the
          portrait-tablet breakpoint (max-[1180px] — 768x1024 has no
          dead width for the rail, see above), collapsed behind a
          disclosure by default instead: RefereeActionBar wraps to
          246px there (its team-injury/quick-action button rows lose
          columns at 768px width) vs 151px at 1024x768 landscape — the
          single biggest lever available in portrait once the rail
          can't apply. `hidden
          max-[1180px]:flex` means the toggle button itself never
          renders at >=1180px, so desktop is unaffected byte-for-byte;
          the content div's base `block` class means it is always
          visible there too. */}
      <div className="w-full">
        <button
          type="button"
          onClick={() => setShowRefereeBar((prev) => !prev)}
          aria-expanded={showRefereeBar}
          data-testid="toggle-referee-actions"
          className="hidden max-[1180px]:flex w-full items-center justify-between gap-2 px-4 py-3 min-h-[44px] mb-2 text-sm font-semibold text-amber-200 bg-slate-800 border border-amber-500/30 rounded-xl hover:bg-slate-900/40 transition-colors"
        >
          <span className="flex items-center gap-2">
            <Flag size={16} aria-hidden="true" />
            {t("refereeActionsToggle", "Referee Actions")}
          </span>
          {showRefereeBar ? (
            <ChevronUp size={16} aria-hidden="true" />
          ) : (
            <ChevronDown size={16} aria-hidden="true" />
          )}
        </button>
        <div className={showRefereeBar ? "block" : "block max-[1180px]:hidden"}>
          <RefereeActionBar
            disabled={cockpitLocked || clockMode !== "EFFECTIVE"}
            switchDisabled={cockpitLocked}
            onActionSelect={handleRefereeAction}
            hasActiveIneffective={hasActiveIneffective}
            ineffectiveTeamLabel={ineffectiveTeamLabel}
            homeTeamShortName={
              match?.home_team?.short_name || match?.home_team?.name
            }
            awayTeamShortName={
              match?.away_team?.short_name || match?.away_team?.name
            }
            onCardSelect={handleCardSelection}
            activeCard={pendingCardType}
            cardsAndSubDisabled={cockpitLocked}
            t={t}
          />
        </div>
      </div>
    </>
  );
  /** One definition for both mounts (rail, or below the pitch in portrait). */
  const matchPeriodSelectorElement = (
    <MatchPeriodSelector
      match={match}
      operatorPeriod={operatorPeriod}
      currentPhase={currentPhase}
      isExtraTime={isExtraTime}
      extraTimeSeconds={extraTimeSeconds}
      globalClock={globalClock}
      isClockRunning={isGlobalClockRunning}
      onTransitionToHalftime={() =>
        guardTransition("Halftime", transitionToHalftime)
      }
      onTransitionToSecondHalf={() =>
        guardTransition("Live_Second_Half", transitionToSecondHalf)
      }
      onTransitionToFulltime={() =>
        guardTransition("Fulltime", transitionToFulltime)
      }
      onTransitionToExtraFirst={() =>
        guardTransition("Live_Extra_First", transitionToExtraFirst)
      }
      onTransitionToExtraHalftime={() =>
        guardTransition("Extra_Halftime", transitionToExtraHalftime)
      }
      onTransitionToExtraSecond={() =>
        guardTransition("Live_Extra_Second", transitionToExtraSecond)
      }
      onTransitionToPenalties={() =>
        guardTransition("Penalties", transitionToPenalties)
      }
      onFinishMatch={() => guardTransition("Completed", finishMatch)}
      transitionDisabled={transitionDisabled}
      penaltiesTransitionDisabled={penaltiesTransitionDisabled}
      transitionReason={transitionReason}
      t={t}
    />
  );
  const railQuickActionsInMain = isRailLayout && isShortViewport;
  /** EPIC-111 judge P3b: below 900px tall the rail cannot hold ACCIONES
   * RAPIDAS (~938px content vs ~740px usable at 1180x820); the same bar then
   * mounts once in the main column under the pitch. Nothing is removed. */
  const railRefereeActionBar = (
    <RefereeActionBar
      disabled={cockpitLocked || clockMode !== "EFFECTIVE"}
      switchDisabled={cockpitLocked}
      onActionSelect={handleRefereeAction}
      hasActiveIneffective={hasActiveIneffective}
      ineffectiveTeamLabel={ineffectiveTeamLabel}
      homeTeamShortName={match?.home_team?.short_name || match?.home_team?.name}
      awayTeamShortName={match?.away_team?.short_name || match?.away_team?.name}
      onCardSelect={handleCardSelection}
      activeCard={pendingCardType}
      cardsAndSubDisabled={cockpitLocked}
      t={t}
      isRailLayout={!railQuickActionsInMain}
    />
  );
  return (
    /*
     * Responsive horizontal-bar layout (user-confirmed, v1.6.0).
     *
     * TOP CONTROL BAR (flex-wrap):
     *   MatchTimerDisplay + (TeamSelector / InstructionBanner) sit side-by-side
     *   at natural sizes. RefereeActionBar spans full width on its own row.
     *   On narrow viewports (<~550 px) each panel wraps to its own row.
     *   overflow-x-clip backstop kills any H-scroll from child overflow —
     *   was `overflow-x-hidden` through v1.64.0; switched to `clip`
     *   (PMA-COCK-123 v1.65.0) because `hidden` on one axis promotes the
     *   other axis to `auto` per the CSS overflow spec, silently turning
     *   this div into `position: sticky`'s containing scroll block for
     *   every descendant — including the rail column below, which needs to
     *   stick to the real viewport instead. `clip` clips identically to
     *   `hidden` for this backstop's own purpose (confirmed live: no
     *   horizontal scrollbar / scrollWidth===clientWidth unchanged at
     *   1024x768, 768x1024, 1440x900) but does not create a scroll
     *   container on either axis, so it doesn't intercept sticky. Matches
     *   the browser floor this cockpit already committed to for iPad
     *   support (PMA-COCK-106: Safari 16+ for `container-type`/`cqw` on
     *   TacticalPlayerNode.tsx — `overflow: clip` shipped in the same
     *   Safari 16.0 release, no new floor introduced).
     *
     * FIELD:
     *   ActionStage fills the full available width below the bar.
     *   TacticalField.tsx owns the actual pitch sizing (its own
     *   PITCH_VIEWPORT_CAP_CLASS constant + comment, not duplicated here to
     *   avoid the two drifting out of sync again — this comment previously
     *   quoted a `min(100%, calc((100vh-360px)*105/68))` formula that was
     *   never the live code, disagreeing with TacticalField.tsx's OWN
     *   equally-stale comment; both were corrected together, 2026-08-14).
     *   The pitch is full-width on tall/portrait screens and on any viewport
     *   wider than 1180px; below that it height-caps on short/wide screens.
     *   This does not by itself guarantee the whole cockpit fits one screen —
     *   see team_findings.md 2026-08-14 for the non-pitch-chrome gap.
     *
     * INLINE ANALYTICS (PMA-COCK-093):
     *   Collapsed-by-default disclosure between the field and the live feed.
     *   AnalyticsView (usePlayerStats recompute + useAdvancedMetrics fetch)
     *   is only mounted while expanded — never mounted while collapsed.
     *
     * LIVE FEED:
     *   Collapsible-height card below the field.
     */
    <div className="flex flex-col gap-4 md:gap-2 pb-20 md:pb-6 overflow-x-clip">
      {/* ── CONTROL STACK (single column — every component full-width) ──
          PMA-COCK-106, 3rd rail-scope revision. TeamSelector +
          InstructionBanner never move — TeamSelector's formation pickers
          are structurally two-sided, built to sit near their own half of
          the pitch, confirmed (not assumed) to be a real spatial-mapping
          problem once an earlier draft put it in the rail anyway.
          MatchTimerDisplay DOES move: stacked here only when NOT in rail
          mode; when `isRailLayout`, it renders inside the rail instead
          (below), alongside ScoreBoard. ── */}
      <div className="flex flex-col gap-2">
        {!isRailLayout && (
          <div className="w-full">
            <MatchTimerDisplay
              match={matchForPhase}
              operatorPeriod={operatorPeriod}
              globalClock={globalClock}
              effectiveClock={effectiveClock}
              ineffectiveClock={ineffectiveClock}
              varClock={varTimeClock}
              timeoutClock={timeoutTimeClock}
              clockMode={clockMode}
              isClockRunning={isGlobalClockRunning}
              locked={cockpitLocked}
              lockReason={lockReason}
              onGlobalStart={handleGlobalClockStartGuarded}
              onGlobalStop={handleGlobalClockStopGuarded}
              onModeSwitch={handleModeSwitchGuarded}
              onVarToggle={handleVarToggle}
              onTimeoutToggle={handleTimeoutToggle}
              isVarActive={isVarActive}
              isTimeoutActive={isTimeoutActive}
              hideResumeButton={showFieldResume || !!isHalftimePhase}
              broadcastMainClock={broadcastMainClock}
              broadcastAddedClock={broadcastAddedClock}
              t={t}
              isCompactStrip={isPortraitStack}
            />
          </div>
        )}

        {/* Team controls + Step instruction — full width, unconditional */}
        <div className="flex flex-col gap-2 w-full">
          <TeamSelector
            isFlipped={manualFieldFlip}
            onFlip={() => setManualFieldFlip((prev) => !prev)}
            onUndo={handleUndoLastEvent}
            undoDisabled={undoDisabled}
            undoCount={undoCount}
            disabled={cockpitLocked}
            viewMode={viewMode}
            setViewMode={setViewMode}
            homeFormation={homeFormation}
            awayFormation={awayFormation}
            applyFormation={applyFormation}
            homeTeamName={match?.home_team?.short_name}
            awayTeamName={match?.away_team?.short_name}
            homeTeamColorInfo={homeTeamColorInfo}
            awayTeamColorInfo={awayTeamColorInfo}
            onTeamColorSaved={onTeamColorSaved}
            match={match}
            liveEvents={liveEvents}
            onUpdateEventData={isAdmin ? handleUpdateEventData : undefined}
            t={t}
          />
          {!isPortraitStack && instructionBannerElement}
        </div>
      </div>
      {/* ── END TOP CONTROL BAR ── */}

      {isRailLayout ? (
        /* ── PMA-COCK-106 RAIL (landscape tablet, e.g. iPad 1024x768),
           3rd revision — ScoreBoard + MatchTimerDisplay + RefereeActionBar,
           beside the pitch instead of stacked above it. Score/clock come
           FIRST in DOM order (highest glance-value, must stay reachable
           without any gesture); RefereeActionBar comes LAST (lowest
           frequency — referee interruptions are rare vs. the primary
           keyboard flow), so if the column's natural content height ever
           exceeds its viewport-bounded window, it is RefereeActionBar that
           falls below the fold, never score/clock/team context. */
        // PMA-COCK-121 (2026-08-16): gap-3 -> max-[1180px]:landscape:gap-1
        // reclaims 8px of horizontal dead width between the rail and the
        // pitch at iPad landscape (client ask: "make bigger the soccer
        // field"). Desktop (>=1180px) and portrait keep gap-3, unchanged.
        <div className="flex flex-row gap-3 max-[1180px]:landscape:gap-1 items-start">
          {/* PMA-COCK-123 (v1.65.0): sticky top-4, not the old
              document-anchored column. The densified rail's own content
              (712px) already fits a 768px viewport — v1.63.0/v1.64.0
              proved raising the internal scroll cap alone doesn't help,
              because the column starts ~416px down an already-scrolling
              page; it needs to stay pinned to the viewport as the operator
              scrolls to the pitch, not just fit its own nested scrollbox.
              Sticky requires no non-`visible`-overflow ancestor between
              here and the real document scroll — this file's own
              `overflow-x-clip` (was `-hidden` through v1.64.0, see that
              div's comment) is what makes this possible. `max-h` is a
              safety-valve floor, not the primary fit mechanism now: 16px
              top offset + 24px bottom clearance (`calc(100vh-40px)`) so a
              longer roster/team name degrades into the rail's own short
              internal scroll instead of running off the bottom of the
              screen, rather than the rail being expected to grow past the
              viewport the way it could pre-sticky. */}
          <div className="sticky top-4 w-[210px] shrink-0">
            <div
              ref={railRef}
              data-testid="cockpit-rail"
              className="w-full flex flex-col gap-2 max-h-[calc(100vh-40px)] supports-[height:100dvh]:max-h-[calc(100dvh-40px)] overflow-y-auto overscroll-contain pb-10"
            >
              <ScoreBoard
                t={t}
                match={match}
                liveScore={liveScore}
                queuedCount={queuedCount}
                pendingAckCount={pendingAckCount}
                transitionError={transitionError}
                toastMessage={toastMessage}
                isRailLayout
              />
              <MatchTimerDisplay
                match={matchForPhase}
                operatorPeriod={operatorPeriod}
                globalClock={globalClock}
                effectiveClock={effectiveClock}
                ineffectiveClock={ineffectiveClock}
                varClock={varTimeClock}
                timeoutClock={timeoutTimeClock}
                clockMode={clockMode}
                isClockRunning={isGlobalClockRunning}
                locked={cockpitLocked}
                lockReason={lockReason}
                onGlobalStart={handleGlobalClockStartGuarded}
                onGlobalStop={handleGlobalClockStopGuarded}
                onModeSwitch={handleModeSwitchGuarded}
                onVarToggle={handleVarToggle}
                onTimeoutToggle={handleTimeoutToggle}
                isVarActive={isVarActive}
                isTimeoutActive={isTimeoutActive}
                hideResumeButton={showFieldResume || !!isHalftimePhase}
                broadcastMainClock={broadcastMainClock}
                broadcastAddedClock={broadcastAddedClock}
                t={t}
                isRailLayout
              />
              {/* PMA-COCK-210 rail relocation (see this file's own
                  `currentPhase` prop-block comment above for the full
                  record) — the phase ladder, right after the clock it
                  controls and before the lowest-frequency referee actions,
                  matching this rail's own "highest glance-value first"
                  ordering rule stated in the comment above this column. */}
              {matchPeriodSelectorElement}
              {!railQuickActionsInMain && railRefereeActionBar}
            </div>
            {railHasMoreBelow && (
              <div
                data-testid="cockpit-rail-scroll-affordance"
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-slate-900 to-transparent rounded-b-xl"
              />
            )}
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            {actionStageElement}
            {railQuickActionsInMain && (
              <div data-testid="cockpit-main-quick-actions">
                {railRefereeActionBar}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {!isPortraitStack && refereeSectionElement}

          {/* ── SOCCER FIELD (full viewport width, height-capped via min() in TacticalField) ── */}
          {actionStageElement}
          {/* ── END SOCCER FIELD ── */}
          {isPortraitStack && (
            <div
              className="flex flex-col gap-2"
              data-testid="cockpit-portrait-below-pitch"
            >
              {instructionBannerElement}
              {matchPeriodSelectorElement}
              {refereeSectionElement}
            </div>
          )}
        </>
      )}

      {/* ── INLINE ANALYTICS DISCLOSURE (PMA-COCK-093, collapsed by default) ──
          Shared by both the rail and stacked layouts — unaffected by
          PMA-COCK-106, rendered once regardless of `isRailLayout`. ── */}
      <div className="bg-slate-800 rounded-xl border border-slate-700/80 overflow-hidden">
        <button
          type="button"
          onClick={() => setIsInlineAnalyticsOpen((prev) => !prev)}
          aria-expanded={isInlineAnalyticsOpen}
          data-testid="toggle-inline-analytics"
          className="flex w-full items-center justify-between gap-2 px-4 py-3 min-h-[44px] text-sm font-semibold text-slate-200 hover:bg-slate-900/40 transition-colors"
        >
          <span className="flex items-center gap-2">
            <BarChart3 size={16} aria-hidden="true" />
            {t("inlineAnalyticsToggle", "Match analytics")}
          </span>
          {isInlineAnalyticsOpen ? (
            <ChevronUp size={16} aria-hidden="true" />
          ) : (
            <ChevronDown size={16} aria-hidden="true" />
          )}
        </button>
        {isInlineAnalyticsOpen && (
          <div className="border-t border-slate-700/80 px-4 py-4">
            <AnalyticsView
              match={match}
              liveEvents={liveEvents}
              queuedEvents={queuedEvents}
              onFieldIds={onFieldIds}
              effectiveClock={effectiveClock}
              globalClock={globalClock}
              broadcastMatchClock={broadcastMatchClock}
              currentPeriod={operatorPeriod}
              matchFinished={
                currentPhase === "COMPLETED" || currentPhase === "PENALTIES"
              }
              ineffectiveClock={ineffectiveClock}
              varClock={varTimeClock}
              timeoutClock={timeoutTimeClock}
              effectiveTime={effectiveTime}
              varTimeSeconds={varTimeSeconds}
              timeoutTimeSeconds={timeoutTimeSeconds}
              ineffectiveBreakdown={ineffectiveBreakdown}
              clockMode={clockMode}
              isVarActive={isVarActive}
              isTimeoutActive={isTimeoutActive}
              t={t}
            />
          </div>
        )}
      </div>
      {/* ── END INLINE ANALYTICS DISCLOSURE ── */}

      {/* PMA-COCK-111: Live Feed removed from the cockpit's render path
          (client request, 2026-08-14 — "we have the revision tab"). The
          LiveEventFeed component itself is untouched and still renders in
          full for the Review tab's own event list (ReviewView.tsx), which
          is now the only place to review events from the cockpit. This
          also retires the PMA-COCK-106 height cap
          ("h-72 md:h-96 lg:h-[32rem] max-[1180px]:h-48") that used to live
          on this wrapper — it goes with the block, not left dangling. */}
    </div>
  );
}
